import { CardVariant } from '@/components/card/types';
import { DynamicCarousel } from '@/components/dynamic-carousel';
import { GlobalFullBleed } from '@/components/ui/global-full-bleed';
import { GlobalGridItem } from '@/components/ui/global-grid-item';
import { useDynamicCarouselCardVariantColumnSpan } from '@/hooks/use-dynamic-carousel-card-variant-column-span';
import { useGridContext } from '@/hooks/use-global-grid-context';
import { useGlobalGridItemStyles } from '@/hooks/use-grid-item-child-styles';
import { DisplayedCarouselItemProvider } from '@/providers/displayed-carousel-item';
import { HomePackItemProvider } from '@/providers/home-pack-item';
import { EnumScreenName } from '@calm/evexia-analytics';
import { CirclePartnerHealthResource } from '@calm/evexia-web-sdk';
import { FullscreenCarousel } from '@rhg/components/carousel/fullscreen';
import ResourceCard from '@rhg/components/resources/resource-card';
import { useTheme } from '@rhg/libs/material';
import { formatDataForActionSheet } from '@rhg/utils/resources';
import { useMemo } from 'react';

const styleCarouselList = {
    mb: 25,
};

const carouselItemWidth = 326;
const maxCarouselItems = 9;

interface ResourcesCarouselCardProps {
    resources: CirclePartnerHealthResource[];
    setActionDialogData: (data: any) => void;
    source?: EnumScreenName;
    partnerDisplayName?: string;
    isDynamic?: boolean;
}

function ResourcesCarouselCard({
    resources,
    setActionDialogData,
    source,
    partnerDisplayName,
    isDynamic = false,
}: ResourcesCarouselCardProps) {
    const theme = useTheme();
    const displayedResources = useMemo(() => {
        // Limit the number of resources displayed to 9
        const hasMoreThanMax = (resources || []).length > maxCarouselItems;
        return hasMoreThanMax
            ? resources.slice(0, maxCarouselItems)
            : resources;
    }, [resources]);
    const gridContext = useGridContext();
    const gridItemChildStyles = useGlobalGridItemStyles();
    const columnSpan = useDynamicCarouselCardVariantColumnSpan(
        CardVariant.CarouselSquare
    );

    if (isDynamic) {
        const { gutter } = gridContext;
        return (
            <GlobalFullBleed>
                <DisplayedCarouselItemProvider
                    displayedItems={displayedResources}
                    isFetched={true}
                >
                    <DynamicCarousel
                        trackBackButton="ResourcesCarouselBack"
                        trackForwardButton="ResourcesCarouselForward"
                        firstItemOffsetPx={gutter}
                        data-testid="resources-carousel-card"
                        sx={styleCarouselList}
                    >
                        {displayedResources.map((resource, index) => (
                            <HomePackItemProvider
                                key={resource.id}
                                rowItemIndex={index}
                                packItemName={resource.name}
                            >
                                <GlobalGridItem span={columnSpan}>
                                    <ResourceCard
                                        isDynamic
                                        variant="card"
                                        resource={resource}
                                        onClick={() =>
                                            setActionDialogData(
                                                formatDataForActionSheet(
                                                    resource,
                                                    source,
                                                    partnerDisplayName
                                                )
                                            )
                                        }
                                        sx={gridItemChildStyles}
                                    />
                                </GlobalGridItem>
                            </HomePackItemProvider>
                        ))}
                    </DynamicCarousel>
                </DisplayedCarouselItemProvider>
            </GlobalFullBleed>
        );
    }

    return (
        <FullscreenCarousel
            trackBackButton="ResourcesCarouselBack"
            trackForwardButton="ResourcesCarouselForward"
            itemsDisplayed={displayedResources.length}
            itemWidth={carouselItemWidth}
            itemSpacing={theme.semantics.Spacing[24]}
            listSx={styleCarouselList}
            data-testid="resources-carousel-card"
            sx={{ width: '100%' }}
            firstItemOffsetPx={32}
        >
            {displayedResources.map((resource) => (
                <ResourceCard
                    key={resource.id}
                    variant="card"
                    resource={resource}
                    onClick={() =>
                        setActionDialogData(
                            formatDataForActionSheet(
                                resource,
                                source,
                                partnerDisplayName
                            )
                        )
                    }
                />
            ))}
        </FullscreenCarousel>
    );
}

export default ResourcesCarouselCard;
